import React from 'react';
import { observable, runInAction } from 'mobx';

import { CeeviewNavigator, NavigatorGoArgs, RedirectModifier } from 'tools/ceeviewNavigator';
import { RedirectConfig } from 'controls/designer/features/redirectOnClick/redirectConfig';
import { RedirectType } from 'controls/designer/features/redirectOnClick/redirectType';
import { State } from 'tools/state';
import { getUrlBuilderForCurrentHash } from 'tools/urlBuilder';
import { DashboardsRouter } from 'areas/dashboards/bundleDescription';
import { openModal } from 'controls/react/ant/antModal';
import { newGuid } from 'tools/guid';
import { translator } from 'core/localization/localization';
import { DesignerContext, DesignerStore } from 'controls/designer/designerStore';
import { DesignerToolbar } from 'controls/designer/components/designerToolbar';
import { Toolbar } from 'controls/react/layout/toolbar';

const i18n = translator();

type NavigatorForRedirectConfigProps = {
	wrappedNavigator: CeeviewNavigator;
	config: RedirectConfig;
	linksRoot?: HTMLElement;
	designerComponent: React.ComponentType;
};

export class NavigatorForRedirectConfig extends CeeviewNavigator {
	id: string;

	constructor(public props: NavigatorForRedirectConfigProps) {
		super();

		if (!this.props.linksRoot) {
			return;
		}

		this.id = newGuid();
		this.props.linksRoot.addEventListener('click', (e) => {
			if (e.target instanceof HTMLElement) {
				const id = e.target.getAttribute('data-navigator-link-id');
				if (this.id == id) {
					const url = e.target.getAttribute('data-navigator-link');
					this.go({ url: url });
				}
			}
		});
	}

	go(args: NavigatorGoArgs) {
		switch (this.props.config?.type) {
			case RedirectType.Custom:
				this.props.wrappedNavigator.go({
					url: this.props.config.link,
					modifier: this.props.config.modifier,
					popup: this.props.config.popup,
				});
				break;

			case RedirectType.Dashboard: {
				let url = '';

				if (this.props.config.modifier == RedirectModifier.Popup) {
					const designerConfig = {
						toolbar: false as const,
					};

					const DashboardDesignerComponent = this.props.designerComponent;

					const title = observable.box(i18n('Dashboard'));
					const innerStore = observable.box<DesignerStore>(null);

					openModal(
						() => ({
							title: (
								<Toolbar title={title.get()}>
									{innerStore.get() != null ? (
										<DesignerContext.Provider value={innerStore.get()}>
											<DesignerToolbar transparent />
										</DesignerContext.Provider>
									) : null}
								</Toolbar>
							),
							footer: null,
							width: this.props.config.popup.width,
							height: this.props.config.popup.height,
							positionType: this.props.config.popup.position,
							resizable: true,
						}),
						<DashboardDesignerComponent
							id={this.props.config.dashboardId}
							mode={'viewer'}
							config={designerConfig}
							dashboardLoaded={(dashboard: { tag?: string }) =>
								runInAction(() => title.set(dashboard?.tag || i18n('Dashboard')))
							}
							onLoaded={(designerComponent: { designer: { store: DesignerStore } }) =>
								runInAction(() => {
									designerComponent.designer.store.toolbar.items = 'zoom-in zoom-out';
									innerStore.set(designerComponent.designer.store);
								})
							}
						/>,
					);
				} else {
					if (State.mainApp?.standalone == true) {
						const urlBuilder = getUrlBuilderForCurrentHash();
						url = DashboardsRouter.standalone(this.props.config.dashboardId, urlBuilder.params.sessionId);
					} else {
						url = DashboardsRouter.viewer(this.props.config.dashboardId);
					}

					this.props.wrappedNavigator.go({
						url: url,
						modifier: this.props.config.modifier,
					});
				}
				break;
			}

			case RedirectType.None:
				break;

			default:
			case RedirectType.Default:
				if (args?.url == null && args?.action == null) return;

				this.props.wrappedNavigator.go({
					url: args.url,
					action: args.action,
					modifier: this.props.config?.modifier == RedirectModifier.None ? args.modifier : this.props.config?.modifier,
					popup: this.props.config?.popup,
				});
				break;
		}
	}

	renderLink(link: string): string {
		if (this.props.config?.type == RedirectType.Default && this.props.config?.modifier == RedirectModifier.None) {
			return `href="#${link}"`;
		} else {
			return `data-navigator-link-id="${this.id}" data-navigator-link="${link}"`;
		}
	}

	canDoDefault() {
		return this.props.config?.type == RedirectType.Default && this.props.config?.modifier == RedirectModifier.None;
	}
}
